import {
  Address,
  beginCell,
  Cell,
  OpenedContract,
  Sender,
  storeStateInit,
  toNano,
} from "@ton/core";
import {
  SendTransactionRequest,
  useTonAddress,
  useTonConnectUI,
} from "@tonconnect/ui-react";
import {
  SaleNftItemConfig,
  saleNftItemTransaction,
} from "../lib/contracts/saleContract";
import {
  checkSaleCode,
  saleTonMasters,
} from "../lib/versions/contracts/sale.code";

import { SaleV1 } from "../lib/versions/contracts/SaleV1";
import { SaleV2 } from "../lib/versions/contracts/SaleV2";
import { SaleV3 } from "../lib/versions/contracts/SaleV3";
import useTonClientStore from "../stores/TonClientStore/useTonClientStore";
import { getAddressToQueryId } from "./methods/getAddressToQueryId";

import {
  fetchSignSale,
  getNFT,
  postListedOnSaleNFT,
  postSalesContractAddress,
  refreshSaleData,
} from "../api/ApiManager";
import { INFT, NFTStatus } from "../entities/NFT/NFTTypes";
import { SaleTicker } from "../entities/Sale/SaleTypes";
import { useUserCartStore } from "../features/UserCart/helpers/useUserCartStore";
import {
  getOtherMarketplaceNetworkFee,
  marketplaceAddress,
  networkFeeValue,
} from "../utils/AppConstants";
import {
  checkOtherMarketplaceTransactions,
  checkTransactionGeneric,
} from "../utils/checkTransactionsGeneric";
import { checkWhileTransaction } from "../utils/checkTransactionsWithWhile";
import useFriendlyAddress, {
  parseToBounceableAddress,
} from "./useFriendlyAddress";

export enum SalePoolOp {
  buy = 2,
  cancel_sale = 3,
  sale = 5,
  price_change = 6,
  comment = 0,
}

const useLaunchSale = () => {
  const tonClientStore = useTonClientStore();
  const [tonConnectUI] = useTonConnectUI();
  const cartStore = useUserCartStore();
  const userAddress = useTonAddress();

  const deployNFTOnAuction = async (
    nftAddress: string,
    beneficiaryAddressString?: string
  ) => {
    if (beneficiaryAddressString === "" || !beneficiaryAddressString) {
      throw new Error("Sorry, no beneficiary address");
    }

    const startAuctionOpCode = 0x487a8e81;

    // Параметры аукциона
    const queryId = 13; // Идентификатор запроса
    const beneficiaryAddress = Address.parse(beneficiaryAddressString);
    const initialMinBid = toNano("2.991"); // Начальная минимальная ставка в TON
    const maxBid = toNano("2.991"); // Максимальная ставка в TON
    const minBidStep = 5; // Минимальный шаг ставки
    const minExtendTime = 3600; // Минимальное время продления в секундах
    const duration = 31536000;

    const config = beginCell()
      .storeAddress(beneficiaryAddress) // Адрес бенефициара
      .storeCoins(initialMinBid) // Начальная минимальная ставка
      .storeCoins(maxBid) // Максимальная ставка
      .storeUint(minBidStep, 8) // Минимальный шаг ставки
      .storeUint(minExtendTime, 32) // Минимальное время продления
      .storeUint(duration, 32) // Длительность аукциона
      .endCell();

    const payload = beginCell()
      .storeUint(0x487a8e81, 32) // Опкод
      .storeUint(queryId, 64)
      .storeRef(config)
      .endCell();

    try {
      const transactionsStartTime = Math.round(Date.now() / 1000);
      const tx: SendTransactionRequest = {
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: nftAddress,
            amount: toNano("0.02").toString(),
            payload: payload.toBoc().toString("base64"),
          },
        ],
      };

      await tonConnectUI.sendTransaction(tx);

      await checkWhileTransaction(
        async () =>
          await checkOtherMarketplaceTransactions(
            Address.parse(nftAddress),
            BigInt(queryId),
            transactionsStartTime,
            startAuctionOpCode,
            tonClientStore
          ),
        async () => null
      );
    } catch (error) {
      throw error;
    }
  };

  const cancelNFTOnAuction = async (nftAddress: string) => {
    const queryID = getAddressToQueryId(nftAddress);
    const payload = beginCell()
      .storeUint(0x371638ae, 32) // Опкод отмены
      .storeUint(queryID, 64)
      .endCell();

    const transactionsStartTime = Math.round(Date.now() / 1000);
    const tx: SendTransactionRequest = {
      validUntil: Math.floor(Date.now() / 1000) + 60,
      messages: [
        {
          address: nftAddress,
          amount: toNano("0.1").toString(),
          payload: payload.toBoc().toString("base64"),
        },
      ],
    };

    await tonConnectUI.sendTransaction(tx);

    await checkWhileTransaction(
      async () =>
        await checkOtherMarketplaceTransactions(
          Address.parse(nftAddress),
          BigInt(queryID),
          transactionsStartTime,
          0x371638ae,
          tonClientStore
        ),
      async () => null
    );
  };

  const fillUpGGAuction = async (
    masterAddress: string,
    amountValue: number
  ) => {
    console.log("nik masterAddress", masterAddress, "amountValue", amountValue);
    const payload = beginCell()
      .storeUint(0, 32)
      .storeStringTail("Bought on OOIA")
      .endCell();

    try {
      const transactionsStartTime = Math.round(Date.now() / 1000);
      const tx: SendTransactionRequest = {
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: masterAddress,
            amount: toNano(amountValue).toString(),
            payload: payload.toBoc().toString("base64"),
          },
        ],
      };

      await tonConnectUI.sendTransaction(tx);

      await checkWhileTransaction(
        async () =>
          await checkOtherMarketplaceTransactions(
            Address.parse(masterAddress),
            BigInt(0),
            transactionsStartTime,
            SalePoolOp.comment,
            tonClientStore
          ),
        async () => null
      );
    } catch (error: any) {
      throw error;
    }
  };

  const cancelGGAuction = async () => {
    const payload = beginCell()
      .storeUint(0, 32)
      .storeStringTail("cancel")
      .endCell();

    const addr = "EQDL6tM_3pyIWu3sf6JHVLyvppYGPNZWIkllvMvUBxpMlX2r";
    const amount = toNano(0.2);

    try {
      const transactionsStartTime = Math.round(Date.now() / 1000);
      const tx: SendTransactionRequest = {
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: addr,
            amount: amount.toString(),
            payload: payload.toBoc().toString("base64"),
          },
        ],
      };

      await tonConnectUI.sendTransaction(tx);

      await checkWhileTransaction(
        async () =>
          await checkOtherMarketplaceTransactions(
            Address.parse(addr),
            BigInt(0),
            transactionsStartTime,
            SalePoolOp.comment,
            tonClientStore
          ),
        async () => null
      );
    } catch (error: any) {
      console.log("nik error", error);
    }
  };

  const fillUpNFTOnAuction = async (nftAddress: string, maxBid: string) => {
    const payload = beginCell()
      .storeUint(0, 32)
      .storeStringTail("Bought on OOIA")
      .endCell();

    try {
      const transactionsStartTime = Math.round(Date.now() / 1000);
      const tx: SendTransactionRequest = {
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: nftAddress,
            amount: maxBid,
            payload: payload.toBoc().toString("base64"),
          },
        ],
      };

      await tonConnectUI.sendTransaction(tx);

      await checkWhileTransaction(
        async () =>
          await checkOtherMarketplaceTransactions(
            Address.parse(nftAddress),
            BigInt(0),
            transactionsStartTime,
            SalePoolOp.comment,
            tonClientStore
          ),
        async () => null
      );
    } catch (error: unknown) {
      throw error;
    }
  };

  const buyNFTOnSaleFromOtherMarketplace = async (
    saleAddress: string,
    fullPrice: bigint,
    contractType: string
  ) => {
    // NOTE: previous realization of NFT purchase
    // const payload = beginCell()
    //   .storeUint(SalePoolOp.buy, 32)
    //   .storeUint(0, 64)
    //   .endCell();

    const payload = beginCell()
      .storeUint(0, 32)
      .storeStringTail("Bought on OOIA")
      .endCell();

    const networkFee = toNano(getOtherMarketplaceNetworkFee(contractType));
    const amount = (fullPrice + networkFee).toString();

    try {
      const transactionsStartTime = Math.round(Date.now() / 1000);
      const tx: SendTransactionRequest = {
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: saleAddress,
            amount: amount,
            payload: payload.toBoc().toString("base64"),
          },
        ],
      };

      await tonConnectUI.sendTransaction(tx);

      await checkWhileTransaction(
        async () =>
          await checkOtherMarketplaceTransactions(
            Address.parse(saleAddress),
            BigInt(0),
            transactionsStartTime,
            SalePoolOp.comment,
            tonClientStore
          ),
        async () => null
      );
    } catch (error: unknown) {
      throw error;
    }
  };

  // NOTE: param name uncorrect. it is not nftAddress but address of sale contract
  const buyNFTOnSale = async (
    nftAddress: string,
    fullPrice: bigint
  ): Promise<void | null> => {
    try {
      if (!tonClientStore.tonClient4 || !tonClientStore.sender) {
        throw new Error("It seems like no ton client");
      }

      const codeVersion = await checkSaleCode(nftAddress);
      if (!codeVersion) {
        throw new Error("It seems like no code version");
      }

      const master: OpenedContract<SaleV1 | SaleV2 | SaleV3> =
        tonClientStore.tonClient4.open(
          codeVersion.wrapper.createFromAddress(Address.parse(nftAddress))
        );

      const networkFee = toNano(networkFeeValue);
      const amount = (fullPrice + networkFee).toString();

      const transactionsStartTime = Math.round(Date.now() / 1000);

      const queryID = getAddressToQueryId(nftAddress);

      const msgBody = beginCell();

      // NOTE: previous realization of NFT purchase
      // msgBody.storeUint(2, 32);
      // msgBody.storeUint(queryID, 64);

      // NOTE: realization with comment
      msgBody.storeUint(0, 32);
      msgBody.storeStringTail("Bought on OOIA");

      const address = Address.parse(nftAddress);

      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 3600,
        messages: [
          {
            address: address.toString({ bounceable: true }),
            amount,
            payload: msgBody.endCell().toBoc().toString("base64"),
          },
        ],
      });

      await checkWhileTransaction(
        async () =>
          await checkTransactionGeneric(
            master,
            BigInt(queryID),
            transactionsStartTime,
            SalePoolOp.comment,
            tonClientStore
          ),
        async () => {
          await refreshSaleData(master.address.toString({ bounceable: true }));
        }
      );
    } catch (error: any) {
      throw error;
    }
  };

  const changeSalePrice = async (
    nftAddress: string,
    royaltyAddress: string,
    marketFeePercent: number,
    royaltyPercent: number,
    price: number
  ) => {
    if (!tonClientStore.tonClient4 || !tonClientStore.sender) {
      throw new Error("It seems like no ton client");
    }

    const codeVersion = await checkSaleCode(nftAddress);
    if (!codeVersion) {
      throw new Error("It seems like no NFT code version");
    }

    const queryId = getAddressToQueryId(nftAddress);

    const master: OpenedContract<SaleV1 | SaleV2 | SaleV3> =
      tonClientStore.tonClient4.open(
        codeVersion.wrapper.createFromAddress(Address.parse(nftAddress))
      );
    const transactionsStartTime = Math.round(Date.now() / 1000);

    if (royaltyAddress === "") {
      throw new Error("royaltyAddress is empty string");
    }

    const newMarketFee = (price * marketFeePercent) / 100;
    const newRoyaltyAmount = (price * royaltyPercent) / 100;

    await tonConnectUI.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 3600,
      messages: [
        {
          address: Address.parse(nftAddress).toRawString(),
          amount: toNano("0.01").toString(),
          payload: beginCell()
            .storeUint(6, 32)
            .storeUint(BigInt(queryId), 64)
            .storeCoins(toNano(price))
            .storeRef(
              beginCell()
                .storeAddress(marketplaceAddress)
                .storeCoins(toNano(newMarketFee))
                .storeAddress(Address.parse(royaltyAddress))
                .storeCoins(toNano(newRoyaltyAmount))
                .endCell()
            )
            .endCell()
            .toBoc()
            .toString("base64"),
        },
      ],
    });

    await checkWhileTransaction(
      async () =>
        await checkTransactionGeneric(
          master,
          BigInt(queryId),
          transactionsStartTime,
          SalePoolOp.price_change,
          tonClientStore
        ),
      async () => {
        await refreshSaleData(master.address.toString({ bounceable: true }));
      }
    );
  };

  const cancelOtherMarketplaceSale = async (saleAddress: string) => {
    const payload = beginCell()
      .storeUint(SalePoolOp.cancel_sale, 32)
      .storeUint(0, 64)
      .endCell();

    const tx: SendTransactionRequest = {
      validUntil: Math.floor(Date.now() / 1000) + 60,
      messages: [
        {
          address: saleAddress,
          amount: toNano("0.2").toString(),
          payload: payload.toBoc().toString("base64"),
        },
      ],
    };
    try {
      const transactionsStartTime = Math.round(Date.now() / 1000);

      await tonConnectUI.sendTransaction(tx);

      await checkWhileTransaction(
        async () =>
          await checkOtherMarketplaceTransactions(
            Address.parse(saleAddress),
            BigInt(0),
            transactionsStartTime,
            SalePoolOp.cancel_sale,
            tonClientStore
          ),
        async () => null
      );
    } catch (error: unknown) {
      throw error;
    }
  };

  const cancelSale = async (transactionAddress: string): Promise<void> => {
    if (!tonClientStore.tonClient4 || !tonClientStore.sender) {
      throw new Error("It seems like there are no ton client");
    }

    const addressStr = transactionAddress;

    const codeVersion = await checkSaleCode(addressStr);
    if (!codeVersion) {
      throw new Error("It seems like there are code version of smart contract");
    }

    const queryId = getAddressToQueryId(addressStr);
    const master: OpenedContract<SaleV1 | SaleV2 | SaleV3> =
      tonClientStore.tonClient4.open(
        codeVersion.wrapper.createFromAddress(Address.parse(addressStr))
      );

    const transactionsStartTime = Math.round(Date.now() / 1000);

    await tonConnectUI.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 3600,
      messages: [
        {
          address: Address.parse(addressStr).toRawString(),
          amount: toNano("0.1").toString(),
          payload: beginCell()
            .storeUint(3, 32)
            .storeUint(BigInt(queryId), 64)
            .endCell()
            .toBoc()
            .toString("base64"),
        },
      ],
    });

    await checkWhileTransaction(
      async () =>
        await checkTransactionGeneric(
          master,
          BigInt(queryId),
          transactionsStartTime,
          SalePoolOp.cancel_sale,
          tonClientStore
        ),
      async () => {
        await refreshSaleData(master.address.toString());
      }
    );
  };

  const getBody: (
    via: Sender,
    value: bigint,
    opts: { queryId: bigint; signature: Buffer }
  ) => Cell = (
    via: Sender,
    value: bigint,
    opts: {
      queryId: bigint;
      signature: Buffer;
    }
  ) => {
    return beginCell()
      .storeUint(5, 32)
      .storeUint(opts.queryId, 64)
      .storeBuffer(opts.signature)
      .endCell();
  };

  const launchSaleClick: (
    price: number,
    marketFee: number,
    royaltyFee: number,
    nftAddressString: string,
    valute: SaleTicker,
    collectionOwner: string,
    gas?: number
  ) => Promise<void> = async (
    price,
    marketFee,
    royaltyFee,
    nftAddressString,
    valute,
    collectionOwner,
    gas
  ) => {
    try {
      if (royaltyFee > 49) {
        throw new Error("Sorry, the royalty fee is above 49");
      }
      const nftAddress: Address = Address.parse(nftAddressString);
      if (
        tonConnectUI.account &&
        tonClientStore.tonClient4 &&
        tonClientStore.sender
      ) {
        const admin: Address = Address.parse(tonConnectUI.account?.address);

        const royaltyAddress = Address.parse(collectionOwner);

        const createdAt: number = Math.round(Date.now() / 1000);

        const data = {
          createdAt: createdAt.toString(),
          marketplaceFeeAddress: marketplaceAddress.toRawString(),
          marketFee: marketFee.toString(),
          marketplaceAddress: marketplaceAddress.toRawString(),
          royaltyAddress: royaltyAddress.toRawString(),
          royaltyFee: royaltyFee.toString(),
          nftAddress: nftAddress.toRawString(),
          nftOwnerAddress: admin.toRawString(),
          price: price.toString(),
        };

        const response = await fetchSignSale("ton", data);

        if (!response) {
          throw new Error("An error occurred while obtaining a Sale signature");
        }

        const publicKeyBuffer = Buffer.from(response.publicKey, "hex");
        const secretKeyBuffer = Buffer.from(response.signature, "hex");

        const masterWrapper = saleTonMasters[saleTonMasters.length - 1].wrapper;
        const masterCode = saleTonMasters[saleTonMasters.length - 1].code;

        const master = tonClientStore.tonClient4.open(
          masterWrapper.createFromConfig(
            {
              createdAt: BigInt(createdAt.toString()),
              marketplaceAddress: marketplaceAddress,
              nftAddress: nftAddress,
              nftOwnerAddress: admin,
              fullPrice: toNano(price),
              feesCell: beginCell()
                .storeAddress(marketplaceAddress)
                .storeCoins((toNano(price) * toNano(marketFee)) / toNano(100))
                .storeAddress(royaltyAddress)
                .storeCoins((toNano(price) * toNano(royaltyFee)) / toNano(100))
                .endCell(),
              publicKey: publicKeyBuffer,
            },
            masterCode
          )
        );

        // TODO: add handling if contract exists
        await postSalesContractAddress(
          master.address.toString({ bounceable: true })
        );

        const queryId = getAddressToQueryId(admin.toString());

        if (valute === SaleTicker.TON) {
          const body: Cell = getBody(tonClientStore.sender, toNano("0.05"), {
            queryId: BigInt(queryId),
            signature: secretKeyBuffer,
          });

          const saleData: SaleNftItemConfig = {
            createdAt,
            nftAddress: nftAddress.toRawString(),
            royaltyAddress: royaltyAddress.toRawString(),
            destinationAddress: marketplaceAddress.toRawString(),
            walletAddress: admin.toRawString(),
            saleAddress: master.address.toRawString(),
            marketplaceFeeAddress: marketplaceAddress.toRawString(),
            price: price.toString(),
            marketFee: marketFee.toString(),
            royaltyFee: royaltyFee.toString(),
            publicKey: publicKeyBuffer.toString(),
            queryId: queryId,
            signature: secretKeyBuffer.toString(),
          };

          const { transaction } = saleNftItemTransaction(saleData);

          const stateInitCell = !master.init
            ? undefined
            : beginCell().store(storeStateInit(master.init)).endCell();

          const gasValue = gas ? gas.toString() : toNano("0.01").toString();

          const tx: SendTransactionRequest = {
            validUntil: Date.now() + 5 * 60 * 1000,
            messages: [
              {
                address: master.address.toString(),
                amount: gasValue,
                payload: body.toBoc().toString("base64"),
                stateInit: stateInitCell?.toBoc().toString("base64"),
              },
              transaction.messages[0],
            ],
          };

          const transactionsStartTime = Math.round(Date.now() / 1000);

          await tonConnectUI.sendTransaction(tx);

          await checkWhileTransaction(
            async () =>
              await checkTransactionGeneric(
                master,
                BigInt(queryId),
                transactionsStartTime,
                SalePoolOp.sale,
                tonClientStore
              ),
            async () => {
              await postListedOnSaleNFT(master.address.toString());
            }
          );
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  const purchaseCartItems = async (items: INFT[]) => {
    const saleAddresses = items
      .slice()
      .map((item) => {
        return {
          nftAddress: item.address,
          saleAddress:
            item.status === NFTStatus.ON_SALE
              ? item.currentSale?.address
              : item.currentOtherSale?.address,
        };
      })
      .filter(
        (item) =>
          item.saleAddress !== undefined && item.nftAddress !== undefined
      );

    try {
      const transactionMessages: {
        address: string;
        amount: string;
        payload?: string;
      }[] = await Promise.all(
        items
          .map((item) => {
            const data =
              item.status === NFTStatus.ON_SALE
                ? item.currentSale
                : item.currentOtherSale;

            if (!data) {
              return null;
            }

            const address = Address.parse(data.address);
            const networkFee =
              item.status === NFTStatus.ON_OTHER_SALE
                ? toNano(
                    getOtherMarketplaceNetworkFee(
                      item.currentOtherSale?.contractType ?? "gg_sale_v3_3"
                    )
                  )
                : toNano(networkFeeValue);
            const amount = (BigInt(data.price!) + networkFee).toString();

            return {
              address: address.toString({ bounceable: true }),
              amount: amount.toString(),
              payload: beginCell()
                .storeUint(0, 32)
                .storeStringTail("Bought on OOIA")
                .endCell()
                .toBoc()
                .toString("base64"),
            };
          })
          .filter((mappedItem) => mappedItem !== null)
      );

      const transactionsStartTime = Math.round(Date.now() / 1000);
      cartStore.setUserRejectsWalletActionStatus(false);

      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 3600,
        messages: transactionMessages,
      });

      for (const addressObject of saleAddresses) {
        if (addressObject.saleAddress === undefined) return;

        await checkWhileTransaction(
          async () =>
            await checkOtherMarketplaceTransactions(
              Address.parse(addressObject.saleAddress ?? ""),
              BigInt(0),
              transactionsStartTime,
              SalePoolOp.comment,
              tonClientStore
            ),
          async () => {
            await refreshSaleData(
              Address.parse(addressObject.saleAddress ?? "").toString({
                bounceable: true,
              })
            );
          }
        );
      }

      for (const addressObject of saleAddresses) {
        let attempts = 0;

        while (attempts < 5) {
          try {
            const fetchedNFT: INFT = await getNFT(
              parseToBounceableAddress(addressObject.nftAddress) ?? ""
            );

            if (
              fetchedNFT &&
              useFriendlyAddress(fetchedNFT.ownerAddress) === userAddress
            ) {
              cartStore.removeItemFromCart(fetchedNFT.address);
              cartStore.addItemToPurchased(fetchedNFT);
              break;
            }
            attempts += 1;
            await new Promise((resolve) => setTimeout(resolve, 5000));
          } catch (error) {
            attempts += 1;
            break;
          }
        }
      }
    } catch (error: any) {
      if (
        error?.message?.includes("rejects") ||
        error.message.includes("Reject") ||
        error.message.includes("Pop-up closed")
      ) {
        cartStore.setUserRejectsWalletActionStatus(true);
        throw error;
      } else {
        throw error;
      }
    }
  };

  return {
    launchSaleClick,
    cancelSale,
    buyNFTOnSale,
    changeSalePrice,
    cancelOtherMarketplaceSale,
    buyNFTOnSaleFromOtherMarketplace,
    fillUpNFTOnAuction,
    deployNFTOnAuction,
    cancelNFTOnAuction,
    purchaseCartItems,
    fillUpGGAuction,
    cancelGGAuction,
  };
};

export default useLaunchSale;
